import type { ICompanyDomain } from '@/api/organization/company-domain/get-company-domain-query'
import type { IRemoveCompanyDomainResult } from '@/api/organization/company-domain/remove-company-domain-mutation'
import type { ITurnOffCompanyDomainSuggestionsResult } from '@/api/organization/company-domain/turn-off-company-domain-suggestions-mutation'

export type ICompanyDomainManagementAction = 'turn_off_suggestions' | 'remove_domain'
type ICompanyDomainRow = ICompanyDomain['domains'][number]

export interface IPreparedCompanyDomainManagementAction {
    action: ICompanyDomainManagementAction
    organizationId: string
    domainId: string
    domainName: string
    enrollmentMode: ICompanyDomainRow['enrollmentMode']
}

export type IConfirmedCompanyDomainManagement = IPreparedCompanyDomainManagementAction

type ICompanyDomainManagementResult = IRemoveCompanyDomainResult | ITurnOffCompanyDomainSuggestionsResult

function normalizeDomainName(domainName: string) {
    return domainName.trim().toLowerCase()
}

function isExactDomain(domain: ICompanyDomainRow, prepared: IPreparedCompanyDomainManagementAction) {
    return (
        domain.id === prepared.domainId && normalizeDomainName(domain.name) === normalizeDomainName(prepared.domainName)
    )
}

/** Resolves an action only from a fresh server-authorized domain row and capability. */
export function getFreshCompanyDomainManagementTarget(
    companyDomain: ICompanyDomain | undefined,
    prepared: IPreparedCompanyDomainManagementAction
) {
    const domain = companyDomain?.domains.find((candidate) => isExactDomain(candidate, prepared))
    if (!domain) return null
    if (prepared.action === 'turn_off_suggestions' && !domain.actions.canTurnOffSuggestions) return null
    if (prepared.action === 'remove_domain' && !domain.actions.canRemove) return null
    return domain
}

/** Confirms a management outcome only from an exact fresh Clerk-backed status response. */
export function hasAuthoritativeCompanyDomainManagementOutcome(
    companyDomain: ICompanyDomain | undefined,
    prepared: IPreparedCompanyDomainManagementAction
) {
    if (!companyDomain) return false
    if (prepared.action === 'remove_domain') {
        const normalizedName = normalizeDomainName(prepared.domainName)
        return !companyDomain.domains.some(
            (domain) => domain.id === prepared.domainId || normalizeDomainName(domain.name) === normalizedName
        )
    }

    const domain = companyDomain.domains.find((candidate) => isExactDomain(candidate, prepared))
    return (
        domain?.isClaimed === true &&
        domain.verificationStatus === 'verified' &&
        domain.enrollmentMode === 'manual_invitation' &&
        domain.suggestionsEnabled === false
    )
}

/** Bridges a backend-confirmed result while the follow-up Clerk status query catches up. */
export function applyConfirmedCompanyDomainManagement(
    domains: ICompanyDomainRow[],
    activeOrganizationId: string | undefined,
    confirmed: IConfirmedCompanyDomainManagement | null
) {
    if (!activeOrganizationId || confirmed?.organizationId !== activeOrganizationId) return domains

    if (confirmed.action === 'remove_domain') {
        const normalizedName = normalizeDomainName(confirmed.domainName)
        return domains.filter(
            (domain) => domain.id !== confirmed.domainId && normalizeDomainName(domain.name) !== normalizedName
        )
    }

    return domains.map((domain) =>
        isExactDomain(domain, confirmed)
            ? {
                  ...domain,
                  enrollmentMode: 'manual_invitation' as const,
                  suggestionsEnabled: false,
                  isConnected: false,
                  actions: {
                      ...domain.actions,
                      canTurnOffSuggestions: false,
                      canTurnOnSuggestions: true,
                  },
              }
            : domain
    )
}

/** Maps stable backend management failures to actionable copy without guessing commit state. */
export function getCompanyDomainManagementFailureMessage(result: ICompanyDomainManagementResult) {
    if (result.reason === 'authorization_changed') {
        return 'Your Company domain permissions changed. Refresh the status before trying again.'
    }
    if (result.reason === 'domain_not_found' || result.reason === 'domain_changed') {
        return 'This domain changed in Clerk. Refresh the status before trying again.'
    }
    if (result.reason === 'domain_not_verified') {
        return 'Only a verified company domain can have automatic enrollment turned off.'
    }
    if (result.reason === 'suppression_not_confirmed') {
        return "We couldn't safely prevent automatic reconnection, so the domain was not removed. Try again."
    }
    return "We couldn't confirm the change in Clerk. Refresh the status and try again."
}

/** Labels the exact Clerk enrollment mode without conflating it with verification. */
export function getClaimedCompanyDomainEnrollmentLabel(domain: ICompanyDomainRow) {
    if (domain.enrollmentMode !== 'manual_invitation' && domain.automaticClaimStatus !== 'enabled') {
        return 'Retry needed'
    }
    if (domain.enrollmentMode === 'automatic_invitation') return 'Automatic invitations'
    return domain.enrollmentMode === 'automatic_suggestion' ? 'Suggestions on' : 'Suggestions off'
}

/** Makes a failed suppression clear explicitly repairable instead of hiding it behind connected state. */
export function getTurnOnCompanyDomainActionLabel(domain: ICompanyDomainRow) {
    return domain.enrollmentMode === 'manual_invitation' || domain.automaticClaimStatus === 'enabled'
        ? 'Turn on suggestions'
        : 'Retry setup'
}

/** Keeps the menu and confirmation copy aligned with the active Clerk enrollment mode. */
export function getTurnOffCompanyDomainActionLabel(enrollmentMode: ICompanyDomainRow['enrollmentMode']) {
    return enrollmentMode === 'automatic_invitation' ? 'Turn off automatic invitations' : 'Turn off suggestions'
}
